/**
 * Google Play Billing via the Digital Goods API (DGAPI).
 *
 * DGAPI is exposed by Chrome when the PWA runs inside a Trusted Web Activity
 * (TWA) that has been built with Play Billing enabled. In a regular browser or
 * PWA, `window.getDigitalGoodsService` is undefined and this module returns
 * `null` so the caller can fall back to Stripe.
 */

import { isRunningInTWA } from "@/lib/twa";

export const GOOGLE_PLAY_BILLING = "https://play.google.com/billing";
export const PRO_PRODUCT_ID = "pro_lifetime";

/** Resolves to a Digital Goods service, or `null` when unavailable. */
export async function getPlayBillingService() {
    if (typeof window === "undefined") return null;
    // Desktop Chrome also exposes `getDigitalGoodsService` but Play Billing
    // is only valid inside the TWA. Gating here prevents desktop from being
    // routed to a Play flow that can never succeed.
    if (!isRunningInTWA()) return null;
    if (typeof window.getDigitalGoodsService !== "function") return null;
    try {
        const service = await window.getDigitalGoodsService(GOOGLE_PLAY_BILLING);
        return service || null;
    } catch (_err) {
        return null;
    }
}

/** True when the current environment supports Play Billing purchases. */
export async function isPlayBillingAvailable() {
    return (await getPlayBillingService()) !== null;
}

/** Localized product details from Google Play (title, price, currency, ...). */
export async function getProDetails() {
    const service = await getPlayBillingService();
    if (!service) return null;
    try {
        const details = await service.getDetails([PRO_PRODUCT_ID]);
        return details?.[0] ?? null;
    } catch (_err) {
        return null;
    }
}

/**
 * Launches the Play purchase sheet and, on success, sends the purchase token
 * to the backend for server-side verification. Throws on cancel or error so
 * the caller can display an appropriate message.
 *
 * @param {(path: string, body: object) => Promise<Response>} apiPost  Function
 *   that POSTs an authenticated JSON request to the FastAPI backend.
 */
export async function buyProLifetime(apiPost) {
    const service = await getPlayBillingService();
    if (!service) {
        throw new Error("Google Play Billing unavailable");
    }

    const details = (await service.getDetails([PRO_PRODUCT_ID]))?.[0];
    if (!details) {
        throw new Error("Product pro_lifetime is not active in Play Console");
    }

    const request = new PaymentRequest(
        [
            {
                supportedMethods: GOOGLE_PLAY_BILLING,
                data: { sku: PRO_PRODUCT_ID },
            },
        ],
        {
            total: {
                label: details.title ?? "Pro à vie",
                amount: {
                    currency: details.currency,
                    value: details.price,
                },
            },
        }
    );

    const response = await request.show();
    const purchaseToken = response.details?.token;

    if (!purchaseToken) {
        await response.complete("fail").catch(() => {});
        throw new Error("Play did not return a purchase token");
    }

    try {
        const res = await apiPost("/billing/google-play/verify", {
            product_id: PRO_PRODUCT_ID,
            purchase_token: purchaseToken,
        });
        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(text || `Verification failed (HTTP ${res.status})`);
        }
        await response.complete("success");
        return await res.json();
    } catch (err) {
        await response.complete("fail").catch(() => {});
        throw err;
    }
}

/**
 * On app launch, reconcile any Play entitlements the backend may have missed
 * (e.g. after a reinstall, device swap, or offline verification).
 */
export async function restorePlayPurchases(apiPost) {
    const service = await getPlayBillingService();
    if (!service) return { restored: 0 };
    let purchases = [];
    try {
        purchases = (await service.listPurchases()) || [];
    } catch (_err) {
        return { restored: 0 };
    }
    let restored = 0;
    for (const purchase of purchases) {
        if (purchase.itemId !== PRO_PRODUCT_ID) continue;
        try {
            const res = await apiPost("/billing/google-play/verify", {
                product_id: PRO_PRODUCT_ID,
                purchase_token: purchase.purchaseToken,
            });
            if (res.ok) restored += 1;
        } catch (_err) {
            // Ignore individual restore failures; app will retry next launch.
        }
    }
    return { restored };
}
